<template>
  <div>
    <h2 class="text-2xl font-bold text-slate-800 mb-6">Configuración</h2>

    <!-- Tabs -->
    <div class="bg-white rounded-lg shadow-sm border border-slate-200 mb-6">
      <nav class="flex border-b border-slate-200 px-4">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          @click="activeTab = tab.id"
          :class="[
            'px-4 py-3 text-sm font-medium transition-colors duration-200 border-b-2',
            activeTab === tab.id
              ? 'border-indigo-500 text-indigo-600'
              : 'border-transparent text-slate-600 hover:text-slate-800'
          ]"
        >
          {{ tab.label }}
        </button>
      </nav>
    </div>

    <!-- Tab content -->
    <div class="space-y-6 max-w-4xl">
      <!-- Logo Configuration -->
      <section v-if="activeTab === 'general'" class="bg-white p-6 rounded-lg shadow-sm border border-slate-200">
        <h3 class="text-lg font-semibold text-slate-800 mb-4">
          Configuración General
        </h3>

        <div class="space-y-6">
          <!-- Logo URL -->
          <div>
            <label class="block text-sm font-medium text-slate-700 mb-2">
              URL del Logo
            </label>
            <div class="space-y-4">
              <input
                v-model="configStore.generalConfig.logoUrl"
                type="url"
                class="input-field"
                placeholder="https://ejemplo.com/logo.png"
              />
              <p class="text-sm text-slate-500">
                Recomendaciones para el logo:
                <ul class="list-disc list-inside mt-2 space-y-1">
                  <li>Formato: PNG o SVG con fondo transparente</li>
                  <li>Dimensiones recomendadas: 200x60 píxeles</li>
                  <li>Tamaño máximo: 100KB</li>
                </ul>
              </p>
              <!-- Logo Preview -->
              <div v-if="configStore.generalConfig.logoUrl" class="mt-4 p-4 bg-slate-50 rounded-lg">
                <p class="text-sm font-medium text-slate-700 mb-2">Vista previa:</p>
                <img 
                  :src="configStore.generalConfig.logoUrl" 
                  alt="Logo Preview"
                  class="h-12 w-auto"
                  @error="handleLogoError"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Orden de categorías -->
      <section v-if="activeTab === 'order'" class="bg-white p-6 rounded-lg shadow-sm border border-slate-200">
        <h3 class="text-lg font-semibold text-slate-800 mb-4">
          Orden de Categorías
        </h3>
        <p class="text-sm text-slate-600 mb-4">
          Arrastra y suelta las categorías para cambiar su orden de visualización
        </p>
        <div class="space-y-2">
          <div
            v-for="categoryId in categoryOrder"
            :key="categoryId"
            class="p-3 bg-slate-50 rounded-lg border border-slate-200 cursor-move"
            draggable="true"
            @dragstart="handleDragStart($event, categoryId)"
            @dragover="handleDragOver"
            @drop="handleDrop($event, categoryId)"
          >
            {{ categoryStore.categories[categoryId] }}
          </div>
        </div>
      </section>

      <!-- Configuración de categorías -->
      <section v-if="activeTab === 'config'" class="space-y-6">
        <!-- Selector de categoría -->
        <div class="bg-white p-6 rounded-lg shadow-sm border border-slate-200">
          <label class="block text-sm font-medium text-slate-700 mb-2">
            Seleccionar Categoría
          </label>
          <select 
            v-model="selectedCategory" 
            class="input-field max-w-xs"
          >
            <option v-for="[id, label] in Object.entries(categoryStore.categories)" 
                    :key="id" 
                    :value="id"
            >
              {{ label }}
            </option>
          </select>
        </div>

        <!-- Configuración de la categoría seleccionada -->
        <div v-if="selectedCategory" class="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
          <div class="p-6 space-y-8">
            <!-- Visualización -->
            <div>
              <h4 class="text-sm font-medium text-slate-700 mb-4">Visualización</h4>
              <div class="space-y-4 pl-4">
                <!-- Modo de visualización -->
                <div>
                  <label class="block text-sm font-medium text-slate-700 mb-2">
                    Modo de visualización
                  </label>
                  <div class="space-y-2">
                    <div v-for="option in displayModeOptions" :key="option.value">
                      <label class="flex items-center">
                        <input
                          type="radio"
                          :value="option.value"
                          :checked="configStore.getCategoryConfig(selectedCategory).displayMode === option.value"
                          @change="() => updateDisplayMode(selectedCategory, option.value)"
                          class="text-indigo-600 focus:ring-indigo-500"
                        />
                        <span class="ml-2 text-sm text-slate-700">{{ option.label }}</span>
                      </label>
                    </div>
                  </div>
                </div>

                <!-- Criterio de formación de precio -->
                <div v-if="configStore.getCategoryConfig(selectedCategory).displayMode === 'single'">
                  <label class="block text-sm font-medium text-slate-700 mb-2">
                    Criterio de formación de precio
                  </label>
                  <div class="space-y-2">
                    <div v-for="option in priceCalculationOptions" :key="option.value">
                      <label class="flex items-center">
                        <input
                          type="radio"
                          :value="option.value"
                          :checked="configStore.getCategoryConfig(selectedCategory).priceCalculationMethod === option.value"
                          @change="() => updatePriceMethod(selectedCategory, option.value)"
                          class="text-indigo-600 focus:ring-indigo-500"
                        />
                        <span class="ml-2 text-sm text-slate-700">{{ option.label }}</span>
                      </label>
                    </div>
                  </div>
                </div>

                <!-- Solo precio por unidad -->
                <div>
                  <label class="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      :checked="configStore.getCategoryConfig(selectedCategory).onlyUnitPrice"
                      @change="(e) => updateOnlyUnitPrice(selectedCategory, (e.target as HTMLInputElement)?.checked ?? false)"
                      class="rounded text-indigo-600 focus:ring-indigo-500"
                    />
                    <span class="text-sm text-slate-700">Mostrar solo precio por unidad</span>
                  </label>
                  <p class="mt-1 text-xs text-slate-500 ml-6">
                    Si está activado, solo se mostrará el precio por unidad en la lista
                  </p>
                </div>
              </div>
            </div>

            <!-- Campos adicionales -->
            <div>
              <h4 class="text-sm font-medium text-slate-700 mb-4">Campos Adicionales</h4>
              <div class="space-y-4 pl-4">
                <label class="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    :checked="configStore.getCategoryConfig(selectedCategory).hasGraduations"
                    @change="(e) => updateHasGraduations(selectedCategory, (e.target as HTMLInputElement)?.checked ?? false)"
                    class="rounded text-indigo-600 focus:ring-indigo-500"
                  />
                  <span class="text-sm text-slate-700">Incluir selección de graduaciones</span>
                </label>

                <label class="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    :checked="configStore.getCategoryConfig(selectedCategory).hasNotes"
                    @change="(e) => updateHasNotes(selectedCategory, (e.target as HTMLInputElement)?.checked ?? false)"
                    class="rounded text-indigo-600 focus:ring-indigo-500"
                  />
                  <span class="text-sm text-slate-700">Incluir campo de observaciones</span>
                </label>

                <label class="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    :checked="configStore.getCategoryConfig(selectedCategory).allowSimilarProducts"
                    @change="(e) => updateAllowSimilarProducts(selectedCategory, (e.target as HTMLInputElement)?.checked ?? false)"
                    class="rounded text-indigo-600 focus:ring-indigo-500"
                  />
                  <span class="text-sm text-slate-700">Permitir productos similares</span>
                </label>

                <label class="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    :checked="configStore.getCategoryConfig(selectedCategory).allowCalculator"
                    @change="(e) => updateAllowCalculator(selectedCategory, (e.target as HTMLInputElement)?.checked ?? false)"
                    class="rounded text-indigo-600 focus:ring-indigo-500"
                  />
                  <span class="text-sm text-slate-700">Permitir calculadora</span>
                </label>
              </div>
            </div>

            <!-- Mensaje especial -->
            <div>
              <h4 class="text-sm font-medium text-slate-700 mb-4">Mensaje Especial</h4>
              <div class="pl-4">
                <input
                  v-model="configStore.getCategoryConfig(selectedCategory).message"
                  type="text"
                  class="input-field"
                  placeholder="Mensaje que se mostrará junto al contador de productos"
                />
                <p class="mt-2 text-xs text-slate-500">
                  Este mensaje se mostrará junto al contador de productos cuando la categoría esté seleccionada
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Save button -->
      <div class="flex justify-end">
        <button 
          @click="handleSave" 
          class="btn-primary"
          :disabled="configStore.loading"
        >
          {{ configStore.loading ? 'Guardando...' : 'Guardar Configuración' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, watch } from 'vue';
import { useConfigStore } from '../stores/configStore';
import { useCategoryStore } from '../stores/categoryStore';
import { useNotificationStore } from '../stores/notificationStore';
import type { DisplayMode, PriceCalculationMethod } from '../stores/configStore';

const configStore = useConfigStore();
const categoryStore = useCategoryStore();
const notificationStore = useNotificationStore();

// Tabs configuration
const tabs = [
  { id: 'general', label: 'General' },
  { id: 'order', label: 'Orden de Categorías' },
  { id: 'config', label: 'Configuración de Categorías' }
];

const activeTab = ref('general');
const selectedCategory = ref('');

// Opciones de visualización
const displayModeOptions = [
  { value: 'single', label: 'Mostrar producto una vez' },
  { value: 'multiple', label: 'Mostrar producto por laboratorio' }
] as const;

// Opciones de cálculo de precios
const priceCalculationOptions = [
  { value: 'lowest', label: 'Menor precio de costo' },
  { value: 'highest', label: 'Mayor precio de costo' },
  { value: 'average', label: 'Promedio de precios' }
] as const;

// Estado local para el orden de categorías
const categoryOrder = ref<string[]>([]);

// Inicializar configuraciones
onMounted(async () => {
  await categoryStore.fetchCategories();
  categoryOrder.value = [...categoryStore.categoryOrder];
  
  // Seleccionar primera categoría por defecto
  if (Object.keys(categoryStore.categories).length > 0) {
    selectedCategory.value = Object.keys(categoryStore.categories)[0];
  }
});

// Guardar configuración
const handleSave = async () => {
  await configStore.saveConfigurations();
  await categoryStore.saveCategoryOrder(categoryOrder.value);
};

// Manejar error de carga del logo
const handleLogoError = () => {
  notificationStore.error('Error al cargar el logo. Verifique la URL');
};

// Actualizar modo de visualización
const updateDisplayMode = (categoryId: string, mode: DisplayMode) => {
  configStore.updateCategoryConfig(categoryId, { displayMode: mode });
};

// Actualizar método de cálculo de precios
const updatePriceMethod = (categoryId: string, method: PriceCalculationMethod) => {
  configStore.updateCategoryConfig(categoryId, { priceCalculationMethod: method });
};

// Actualizar opciones de graduaciones y notas
const updateHasGraduations = (categoryId: string, value: boolean) => {
  configStore.updateCategoryConfig(categoryId, { hasGraduations: value });
};

const updateHasNotes = (categoryId: string, value: boolean) => {
  configStore.updateCategoryConfig(categoryId, { hasNotes: value });
};

const updateAllowSimilarProducts = (categoryId: string, value: boolean) => {
  configStore.updateCategoryConfig(categoryId, { allowSimilarProducts: value });
};

const updateOnlyUnitPrice = (categoryId: string, value: boolean) => {
  configStore.updateCategoryConfig(categoryId, { onlyUnitPrice: value });
};

const updateAllowCalculator = (categoryId: string, value: boolean) => {
  configStore.updateCategoryConfig(categoryId, { allowCalculator: value });
};

// Manejar reordenamiento de categorías
const handleDragStart = (e: DragEvent, categoryId: string) => {
  if (e.dataTransfer) {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', categoryId);
  }
};

const handleDrop = (e: DragEvent, targetId: string) => {
  e.preventDefault();
  const draggedId = e.dataTransfer?.getData('text/plain');
  if (!draggedId || draggedId === targetId) return;

  const newOrder = [...categoryOrder.value];
  const draggedIndex = newOrder.indexOf(draggedId);
  const targetIndex = newOrder.indexOf(targetId);

  newOrder.splice(draggedIndex, 1);
  newOrder.splice(targetIndex, 0, draggedId);
  categoryOrder.value = newOrder;
};

const handleDragOver = (e: DragEvent) => {
  e.preventDefault();
};

// Watch para cambios en el orden y guardar automáticamente
watch(categoryOrder, async (newOrder) => {
  if (newOrder.length > 0) {
    await categoryStore.saveCategoryOrder(newOrder);
  }
}, { deep: true });
</script>